import React, { useState, ReactNode, useRef } from 'react';
import classNames from 'classnames';

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  onChange?: (tabId: string) => void;
  className?: string;
  variant?: 'default' | 'contained' | 'pills';
  color?: 'primary' | 'secondary' | 'neutral';
  fullWidth?: boolean;
  contentClassName?: string;
  tabsPosition?: 'top' | 'bottom';
  orientation?: 'horizontal' | 'vertical';
  size?: 'small' | 'medium' | 'large';
  testId?: string;
  enableSwipe?: boolean; // Enable swipe navigation between tabs
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  onChange,
  className = '',
  variant = 'default',
  color = 'primary',
  fullWidth = false,
  contentClassName = '',
  tabsPosition = 'top',
  orientation = 'horizontal',
  size = 'medium',
  testId,
  enableSwipe = true,
}) => {
  const [activeTab, setActiveTab] = useState<string>(defaultTab || (tabs.length > 0 ? tabs[0].id : ''));
  const contentRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    if (onChange) {
      onChange(tabId);
    }
  };

  // Navigate to next/previous tab
  const navigateToTab = (direction: 'next' | 'prev') => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    let newIndex: number;
    
    if (direction === 'next') {
      newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0; // Loop to start
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1; // Loop to end
    }
    
    const newTab = tabs[newIndex];
    if (newTab && !newTab.disabled) {
      handleTabClick(newTab.id);
    }
  };

  // Touch event handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enableSwipe || orientation === 'vertical') return;
    
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!enableSwipe || orientation === 'vertical') return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;
    
    // Only consider horizontal swipes (deltaX > deltaY)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe right - go to previous tab
        navigateToTab('prev');
      } else {
        // Swipe left - go to next tab
        navigateToTab('next');
      }
    }
  };

  // Keyboard navigation support
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (orientation === 'horizontal') {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateToTab('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateToTab('next');
      }
    } else {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateToTab('prev');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateToTab('next');
      }
    }
  };

  // Get the color classes based on variant and color
  const getColorClasses = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) {
      return 'text-gray-400 dark:text-gray-400 cursor-not-allowed';
    }

    if (isActive) {
      switch (variant) {
        case 'contained':
          switch (color) {
            case 'primary':
              return 'bg-blue-600 text-white';
            case 'secondary':
              return 'bg-purple-600 text-white';
            case 'neutral':
              return 'bg-gray-700 dark:bg-gray-600 text-white';
            default:
              return 'bg-blue-600 text-white';
          }
        case 'pills':
          switch (color) {
            case 'primary':
              return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100';
            case 'secondary':
              return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100';
            case 'neutral':
              return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100';
            default:
              return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100';
          }
        default:
          switch (color) {
            case 'primary':
              return 'text-blue-600 dark:text-blue-300 border-b-2 border-blue-600 dark:border-blue-300';
            case 'secondary':
              return 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400';
            case 'neutral':
              return 'text-gray-800 dark:text-gray-200 border-b-2 border-gray-800 dark:border-gray-200';
            default:
              return 'text-blue-600 dark:text-blue-300 border-b-2 border-blue-600 dark:border-blue-300';
          }
      }
    } else {
      return 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100';
    }
  };

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'text-xs py-1 px-2';
      case 'large':
        return 'text-lg py-3 px-4';
      default:
        return 'text-sm py-2 px-3';
    }
  };

  const tabsList = (
    <div
      className={classNames(
        'flex',
        {
          'flex-row': orientation === 'horizontal',
          'flex-col': orientation === 'vertical',
          'border-b border-gray-200 dark:border-gray-700': variant === 'default' && orientation === 'horizontal',
          'border-r border-gray-200 dark:border-gray-700': variant === 'default' && orientation === 'vertical',
          'bg-gray-100 dark:bg-gray-800 p-1 rounded-md': variant === 'contained' || variant === 'pills',
          'justify-between': fullWidth && orientation === 'horizontal',
        },
        className
      )}
      role="tablist"
      data-testid={testId}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`tabpanel-${tab.id}`}
          id={`tab-${tab.id}`}
          disabled={tab.disabled}
          className={classNames(
            'font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
            getSizeClasses(),
            getColorClasses(activeTab === tab.id, !!tab.disabled),
            {
              'flex-grow text-center': fullWidth && orientation === 'horizontal',
              'rounded-md': variant === 'pills',
              'first:rounded-l-md last:rounded-r-md': variant === 'contained' && orientation === 'horizontal',
              'first:rounded-t-md last:rounded-b-md': variant === 'contained' && orientation === 'vertical',
              'mr-4': orientation === 'horizontal' && variant === 'default',
              'mb-4': orientation === 'vertical' && variant === 'default',
              'mr-1': orientation === 'horizontal' && (variant === 'contained' || variant === 'pills'),
              'mb-1': orientation === 'vertical' && (variant === 'contained' || variant === 'pills'),
            }
          )}
          onClick={() => !tab.disabled && handleTabClick(tab.id)}
        >
          {tab.icon && (
            <span className="mr-2">
              {/* Icon implementation - use your icon component here */}
              {tab.icon}
            </span>
          )}
          {tab.label}
        </button>
      ))}
    </div>
  );

  // Get the content of the active tab
  const activeContent = tabs.find((tab) => tab.id === activeTab)?.content;

  return (
    <div
      className={classNames('w-full', {
        'flex flex-col': orientation === 'horizontal',
        'flex flex-row': orientation === 'vertical',
      })}
    >
      {tabsPosition === 'top' && orientation === 'horizontal' && tabsList}
      {orientation === 'vertical' && tabsList}
      
      <div
        ref={contentRef}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        id={`tabpanel-${activeTab}`}
        className={classNames(
          'mt-4',
          {
            'flex-grow': orientation === 'vertical',
            'ml-4': orientation === 'vertical',
          },
          contentClassName
        )}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {activeContent}
      </div>
      
      {tabsPosition === 'bottom' && orientation === 'horizontal' && (
        <div className="mt-4">{tabsList}</div>
      )}
    </div>
  );
};

export default Tabs;